import type { ComputedMetrics } from "@/types/metrics";
import { Card } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { METRIC_TOOLTIPS } from "@/lib/metricTooltips";

interface BarRowProps {
  seenRate: number;
  blockoffRate: number;
  availRate: number;
  otherRate?: number;
  seenHrs?: number;
  otherHrs?: number;
  availHrs?: number;
  blockHrs?: number;
  compact?: boolean;
  dimmed?: boolean;
}

interface SegmentProps {
  pct: string;
  visualPct: number;
  color: string;
  textSize: string;
}

function Segment({ pct, visualPct, color, textSize }: SegmentProps) {
  return (
    <div
      className={`flex items-center justify-center ${textSize} font-medium text-white overflow-hidden`}
      style={{ width: `${visualPct}%`, background: color, minWidth: 0 }}
    >
      {visualPct > 5 ? `${pct}%` : ""}
    </div>
  );
}

interface RowItem {
  label: string;
  pct: string;
  color: string;
  hours?: number;
}

function BarRow({
  seenRate,
  blockoffRate,
  availRate,
  otherRate,
  seenHrs,
  otherHrs,
  availHrs,
  blockHrs,
  compact,
  dimmed,
}: BarRowProps) {
  const seenPct = (seenRate * 100).toFixed(1);
  const otherPct = ((otherRate ?? 0) * 100).toFixed(1);
  const availPct = (availRate * 100).toFixed(1);
  const blockPct = (blockoffRate * 100).toFixed(1);
  const h = compact ? "h-5" : "h-9";
  const textSize = compact ? "text-[10px]" : "text-[12px]";
  const opacity = dimmed ? "opacity-55" : "";

  // When avail is negative (double-booked), scale seen/other/block to fill 100%
  // but keep the real percentages as displayed labels and tooltip values.
  //
  // No longer expected to fire: available time is read from available_minutes,
  // which the source never reports below zero, and the three buckets now sum to
  // total_worked_minutes exactly. It was reachable when avail was
  // scheduled - seen - blockoff, which went negative on 284 rows in a sample
  // week. Kept as a guard because the column is not ours -- without it a
  // negative would render a bar with a segment wider than the bar.
  const isOverbooked = availRate < 0;
  const positiveSum = seenRate + (otherRate ?? 0) + blockoffRate;
  const scale = isOverbooked && positiveSum > 0 ? 100 / (positiveSum * 100) : 1;
  const seenVisual = isOverbooked ? Number(seenPct) * scale : Number(seenPct);
  const otherVisual = isOverbooked ? Number(otherPct) * scale : Number(otherPct);
  const availVisual = isOverbooked ? 0 : Number(availPct);
  const blockVisual = isOverbooked ? Number(blockPct) * scale : Number(blockPct);

  const tooltipRows: RowItem[] = [
    {
      label: "Pets seen",
      pct: seenPct,
      color: "var(--perf-green)",
      hours: seenHrs,
    },
    ...(otherRate !== undefined
      ? [
          {
            label: "Other appt types",
            pct: otherPct,
            color: "var(--brand-mid-teal)",
            hours: otherHrs,
          },
        ]
      : []),
    {
      label: "Block-offs",
      pct: blockPct,
      color: "var(--perf-red)",
      hours: blockHrs,
    },
    {
      label: "Available time",
      pct: availPct,
      color: "var(--perf-gray)",
      hours: availHrs,
    },
  ];

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={`flex rounded-md overflow-hidden ${h} ${opacity} cursor-default`}>
            <Segment
              pct={seenPct}
              visualPct={seenVisual}
              color="var(--perf-green)"
              textSize={textSize}
            />
            {otherRate !== undefined && (
              <Segment
                pct={otherPct}
                visualPct={otherVisual}
                color="var(--brand-mid-teal)"
                textSize={textSize}
              />
            )}
            <Segment
              pct={blockPct}
              visualPct={blockVisual}
              color="var(--perf-red)"
              textSize={textSize}
            />
            <Segment
              pct={availPct}
              visualPct={availVisual}
              color="var(--perf-gray)"
              textSize={textSize}
            />
          </div>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          showArrow={false}
          className="bg-white border border-black/10 shadow-lg p-2"
        >
          <div className="flex flex-col gap-1 text-[12px]">
            {tooltipRows.map((row) => (
              <div key={row.label} className="flex items-center gap-2">
                <span
                  className="w-2 h-2 rounded-[2px] shrink-0"
                  style={{ background: row.color }}
                />
                <span className="text-gray-700 font-medium w-[110px]">{row.label}</span>
                <span className="tabular-nums text-gray-500 w-[42px] text-right">{row.pct}%</span>
                {row.hours !== undefined && (
                  <span className="tabular-nums text-gray-400">· {row.hours.toFixed(1)} hrs</span>
                )}
              </div>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

interface UtilizationBreakdownProps {
  current: ComputedMetrics;
  priorYear: ComputedMetrics;
  nvaAvg: ComputedMetrics;
  typeFilterActive?: boolean;
}

// Dotted underline because a tooltip nobody knows is there is not a tooltip.
// The bar itself is discoverable by being the thing you are reading; a legend
// entry is not.
function LegendItem({
  color,
  label,
  definition,
}: {
  color: string;
  label: string;
  definition: string;
}) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex cursor-default items-center gap-1.5 text-[12px] text-gray-500">
            <span className="h-2 w-2 rounded-[2px]" style={{ background: color }} />
            <span className="decoration-dotted underline-offset-2 hover:underline">{label}</span>
          </div>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          showArrow={false}
          className="max-w-[280px] border border-black/10 bg-white text-gray-600 shadow-lg"
        >
          {definition}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function UtilizationBreakdown({
  current,
  priorYear,
  nvaAvg,
  typeFilterActive,
}: UtilizationBreakdownProps) {
  return (
    <Card className="px-5 py-4 gap-0 shadow-none border-black/10 mb-7">
      <div className="text-[11px] text-gray-500 mb-1.5 font-medium">Current period</div>
      <BarRow
        seenRate={current.petSeenRate}
        blockoffRate={current.blockoffRate}
        availRate={current.availableTimeRate}
        otherRate={typeFilterActive ? current.otherTypesRate : undefined}
        seenHrs={current.avgSeenHoursPerDvmPerDay}
        otherHrs={current.avgOtherTypesHoursPerDvmPerDay}
        availHrs={current.avgAvailHoursPerDvmPerDay}
        blockHrs={current.avgBlockoffHoursPerDvmPerDay}
      />

      <div className="text-[11px] text-gray-400 mt-3 mb-1">Prior year</div>
      <BarRow
        seenRate={priorYear.petSeenRate}
        blockoffRate={priorYear.blockoffRate}
        availRate={priorYear.availableTimeRate}
        otherRate={typeFilterActive ? priorYear.otherTypesRate : undefined}
        seenHrs={priorYear.avgSeenHoursPerDvmPerDay}
        otherHrs={priorYear.avgOtherTypesHoursPerDvmPerDay}
        availHrs={priorYear.avgAvailHoursPerDvmPerDay}
        blockHrs={priorYear.avgBlockoffHoursPerDvmPerDay}
        compact
        dimmed
      />

      <div className="text-[11px] text-gray-400 mt-2 mb-1">NVA Avg. (Eligible Sites)</div>
      <BarRow
        seenRate={nvaAvg.petSeenRate}
        blockoffRate={nvaAvg.blockoffRate}
        availRate={nvaAvg.availableTimeRate}
        otherRate={typeFilterActive ? nvaAvg.otherTypesRate : undefined}
        seenHrs={nvaAvg.avgSeenHoursPerDvmPerDay}
        otherHrs={nvaAvg.avgOtherTypesHoursPerDvmPerDay}
        availHrs={nvaAvg.avgAvailHoursPerDvmPerDay}
        blockHrs={nvaAvg.avgBlockoffHoursPerDvmPerDay}
        compact
        dimmed
      />

      {/* The definitions hang off the LEGEND, not the bar segments: the bar
          already owns a tooltip listing all four figures, and a segment
          tooltip inside it would fight for the same hover. The legend also
          carries the percentage wording rather than the time wording, because
          that is what this chart plots -- the hours phrasing lives on the
          Utilization cards below. */}
      <div className="flex gap-4 mt-3">
        <LegendItem
          color="var(--perf-green)"
          label="Pets seen"
          definition={METRIC_TOOLTIPS.seenPct}
        />
        {typeFilterActive && (
          <LegendItem
            color="var(--brand-mid-teal)"
            label="Other appt types"
            definition={METRIC_TOOLTIPS.otherTypes}
          />
        )}
        <LegendItem
          color="var(--perf-red)"
          label="Block-offs"
          definition={METRIC_TOOLTIPS.blockoffPct}
        />
        <LegendItem
          color="var(--perf-gray)"
          label="Available time"
          definition={METRIC_TOOLTIPS.availablePct}
        />
      </div>
    </Card>
  );
}
